import { OnInit, AfterViewInit, Renderer2, ChangeDetectorRef, OnDestroy, ElementRef, EventEmitter } from '@angular/core';
import { BdGraphFactory } from 'src/lib/graph/graph.model';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TreeTheme } from '../tree/tree.component';
import { EngineService } from 'src/app/engine/engine.service';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { StoryState } from 'src/app/story/state/story.state';
import { Store } from '@ngxs/store';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
export var TreeSearchDisplayMode;
(function (TreeSearchDisplayMode) {
    TreeSearchDisplayMode[TreeSearchDisplayMode["List"] = 0] = "List";
    TreeSearchDisplayMode[TreeSearchDisplayMode["Tree"] = 1] = "Tree";
})(TreeSearchDisplayMode || (TreeSearchDisplayMode = {}));
var TreeSearchComponent = /** @class */ (function () {
    function TreeSearchComponent(renderer, changeDetectorRef, elementRef, engineService, graphService, store) {
        this.renderer = renderer;
        this.changeDetectorRef = changeDetectorRef;
        this.elementRef = elementRef;
        this.engineService = engineService;
        this.graphService = graphService;
        this.store = store;
        this._query = '';
        this._displayMode = TreeSearchDisplayMode.List;
        this.displayModeChange = new EventEmitter();
        this.theme = TreeTheme.Dark;
        this.treeSearchDisplayMode = TreeSearchDisplayMode;
        this.searchResults = [];
        this.searchHeight = 0;
        this.ngUnsubscribe = new Subject();
    }
    Object.defineProperty(TreeSearchComponent.prototype, "query", {
        get: function () {
            return this._query;
        },
        set: function (value) {
            this._query = value || '';
            this.handleQueryChange();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TreeSearchComponent.prototype, "displayMode", {
        get: function () {
            return this._displayMode;
        },
        set: function (value) {
            if (value == null) {
                return;
            }
            this._displayMode = value;
            this.handleQueryChange();
            this.displayModeChange.emit(this.displayMode);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TreeSearchComponent.prototype, "viewGraph", {
        get: function () {
            return this._viewGraph;
        },
        set: function (value) {
            this._viewGraph = value;
            this.leafNodes = this.engineService.getLeaves();
            // create a pruned version of the viewGraph for interested consumers.
            this.buildPrunedViewGraph(this.displayMode);
            this.handleQueryChange();
            this.changeDetectorRef.markForCheck();
        },
        enumerable: true,
        configurable: true
    });
    TreeSearchComponent.prototype.ngOnInit = function () {
    };
    TreeSearchComponent.prototype.buildPrunedViewGraph = function (mode) {
        // prune the graph according to chapter show objects
        var currentChapterId = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterId);
        var chapterShowObjects = this.store
            .selectSnapshot(StoryState.getStory)
            .chapters.find(function (c) { return c.id === currentChapterId; })
            .showObjects;
        var prunedViewGraph = this.engineService.createViewGraph(chapterShowObjects, true // always prune on view page
        );
        this.prunedViewGraph = prunedViewGraph;
    };
    TreeSearchComponent.prototype.ngAfterViewInit = function () {
        var _this = this;
        // Window has resized, let the 3D canvas know
        this.renderer.listen('window', 'resize', function (event) {
            _this.resize();
        });
    };
    TreeSearchComponent.prototype.ngOnDestroy = function () {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    };
    TreeSearchComponent.prototype.handleQueryChange = function () {
        var searchResults = this.search(this._query, this.displayMode === TreeSearchDisplayMode.List
            ? this.prunedViewGraph
            : this.viewGraph);
        if (this.displayMode === TreeSearchDisplayMode.Tree) {
            this.searchGraph = this.getTree(searchResults);
        }
        this.searchResults = searchResults;
        this.resize();
    };
    // starting with the leaf nodes matching a query, build a viw graph from the bottom up
    TreeSearchComponent.prototype.getTree = function (searchResults) {
        var _this = this;
        var newGraph = BdGraphFactory();
        searchResults.forEach(function (node) {
            _this.graphService.addNode(newGraph, node);
        });
        // work up the ancestor list for each node
        searchResults.forEach(function (node) {
            var ancestors = _this.graphService.getAncestors(_this.viewGraph, node.id, false);
            var prevnode = node;
            ancestors.every(function (ancestor) {
                var addnodes = _this.graphService.getNodes(newGraph, [ancestor]);
                var addnode = null;
                if (addnodes.length === 0) {
                    addnode = _this.graphService.getNodes(_this.viewGraph, [ancestor])[0];
                    if (addnode.depth === 0) {
                        _this.graphService.addRootNode(newGraph, addnode);
                    }
                    else {
                        _this.graphService.addNode(newGraph, addnode);
                    }
                }
                else {
                    addnode = addnodes[0];
                }
                // build edges up the tree, skip to the next leaf node if we hit an existing edge
                var keepClimbing = _this.graphService.addEdge(newGraph, addnode.id, prevnode.id);
                prevnode = addnode;
                return keepClimbing;
            });
        });
        return newGraph;
    };
    TreeSearchComponent.prototype.resize = function () {
        var _this = this;
        this.getSearchHeight()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(function (height) {
            _this.searchHeight = height;
            _this.changeDetectorRef.markForCheck();
        });
    };
    TreeSearchComponent.prototype.getSearchHeight = function () {
        var _this = this;
        var e = this.elementRef;
        return new Observable(function (observer) {
            setTimeout(function () {
                var offsetHeight = _this.elementRef.nativeElement.offsetHeight;
                if (offsetHeight != null) {
                    observer.next(offsetHeight);
                    observer.complete();
                }
            });
        });
    };
    // return a sorted array of leaf nodes matching a query
    TreeSearchComponent.prototype.search = function (query, viewGraph) {
        var _this = this;
        if (viewGraph === undefined) {
            return [];
        }
        query = query.toLowerCase();
        var results = Object.values(viewGraph.nodes)
            .filter(function (node) { return _this.leafNodes.indexOf(node.id) >= 0; })
            .filter(function (node) { return query.length === 0 ? true : node.label.toLowerCase().indexOf(query) >= 0; })
            .sort(function (a, b) {
            return a.label.localeCompare(b.label);
        });
        return results;
    };
    TreeSearchComponent.prototype.trackTreeNode = function (index, node) {
        return node.id;
    };
    return TreeSearchComponent;
}());
export { TreeSearchComponent };
